"use client";

import { useCalendarContext } from "../calendar-context";
import { useNow } from "../hooks/use-now";
import { formatTime } from "../utils";

// =============================================================================
// Current time markers of the week view. Isolated leaves: the minute tick only
// re-renders them, never the grid.
// =============================================================================

/** Position of `now` in the visible hours, in %, or null outside of them */
function useNowPosition(): { now: Date; top: number } | null {
  const { minHour, maxHour } = useCalendarContext();
  const now = useNow();
  const minutes = now.getHours() * 60 + now.getMinutes() - minHour * 60;
  const totalMinutes = (maxHour - minHour) * 60;
  if (minutes < 0 || minutes > totalMinutes) return null;
  return { now, top: (minutes / totalMinutes) * 100 };
}

/** Line across today's column */
export function NowLine() {
  const position = useNowPosition();
  if (!position) return null;

  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-x-0 z-3 flex -translate-y-1/2 items-center"
      style={{ top: `${position.top}%` }}
    >
      <span className="-ml-1 size-2 shrink-0 rounded-full bg-brand" />
      <span className="h-px flex-1 bg-brand" />
    </div>
  );
}

/** Time label in the hours gutter, aligned with the line */
export function NowLabel() {
  const { locale } = useCalendarContext();
  const position = useNowPosition();
  if (!position) return null;

  return (
    <span
      aria-hidden
      className="absolute right-2 z-1 -translate-y-1/2 rounded-sm bg-background px-0.5 text-xs leading-4 font-medium whitespace-nowrap text-brand tabular-nums"
      style={{ top: `${position.top}%` }}
    >
      {formatTime(position.now, locale)}
    </span>
  );
}
